import React from 'react'
import { __DEV__ } from '@mote/helpers/constants'
import type { SystemProps } from '@xstyled/styled-components'
import styled, { x } from '@xstyled/styled-components'
import { forwardRef } from '../../system'

const fallbackIcon = {
  path: (
    <g stroke='currentColor' strokeWidth='1.5'>
      <path
        strokeLinecap='round'
        fill='none'
        d='M9,9a3,3,0,1,1,4,2.829,1.5,1.5,0,0,0-1,1.415V14.25'
      />
      <path
        fill='currentColor'
        strokeLinecap='round'
        d='M12,17.25a.375.375,0,1,0,.375.375A.375.375,0,0,0,12,17.25h0'
      />
      <circle fill='none' strokeMiterlimit='10' cx='12' cy='12' r='11.25' />
    </g>
  ),
  viewBox: '0 0 24 24'
}

type XSvgKeys = keyof Omit<SystemProps, 'children'>

export interface IconProps
  extends Omit<React.SVGAttributes<SVGElement>, XSvgKeys>,
    SystemProps {}

export const Icon = forwardRef<IconProps, 'svg'>((props, ref) => {
  const {
    as: Comp,
    focusable = false,
    role = 'presentation',
    color = 'currentColor',
    width = '1em',
    height = '1em',
    viewBox = '0 0 24 24',
    children,
    className,
    ...rest
  } = props

  const shared: any = {
    ref,
    focusable,
    role,
    className,
    w: width,
    h: height,
    display: 'inline-block',
    lineHeight: height ?? '1em',
    flexShrink: 0,
    color,
    ...rest
  }

  const _viewBox = viewBox ?? fallbackIcon.viewBox

  if (Comp) {
    return <Root {...shared} as={Comp} />
  }

  const _path = (children ?? fallbackIcon.path) as React.ReactNode

  return (
    <x.svg
      xmlns='http://www.w3.org/2000/svg'
      verticalAlign='middle'
      viewBox={_viewBox}
      {...shared}
    >
      {_path}
    </x.svg>
  )
})

if (__DEV__) {
  Icon.displayName = 'Icon'
}

const Root = styled.svgBox.withConfig({
  shouldForwardProp: prop => !['as'].includes(prop)
})<IconProps>``
